import { Add, InfoOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CommonDialog from '../../common/CommonDialog';
import { deleteInventoryApi, getInvcentoryPartDetailAPI, getInventoryBatchDetailApi, updateInventoryApi } from '../../redux/Inventory/action';
import { INVENTORY_TYPES, PART_TYPES, TableRowsLoader } from '../../utility/Constant';
import { filterUndefined, formatNumberAsWhole } from '../../utility/helpers/commonFunction';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import InputWithIcon from '../Common/InputWithIcon';
import PartUsedInvoiceModal from '../Suppliers/SupplierInvoice/PartUsedInvoiceModal';
import AddInventoryModal from './AddInventory';

/**
 * Styles for common UI elements
 */
const styles = {
    // Dialog header styles
    dialogHeader: {
        height: '64px',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    // Dialog content styles
    dialogContent: {
        padding: '24px!important',
    },

    // Table header cell styles
    tableHeaderCell: {
        padding: '8px 12px',
        height: '40px',
        lineHeight: 'normal',
        fontWeight: 500,
        background: '#F8F8F8',
        color: '#000',
        fontSize: '14px',
    },

    // Table header cell styles
    tableHeaderTotalCell: {
        padding: '3.5px 12px',
        height: '32px!important',
        lineHeight: 'normal',
        fontWeight: 500,
        background: '#F8F8F8',
    },

    // Table body cell styles
    tableBodyCell: {
        padding: '7.5px 12px',
        height: '48px !important',
        minHeight: '48px !important',
        maxHeight: '48px !important',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
    },

    // Typography for table cell content
    tableCellTypography: {
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.15px',
    },

    // Summary box container style
    summaryBoxContainer: {
        width: '100%',
        borderRadius: '4px',
        overflow: { xs: 'auto', md: 'hidden' },
        mb: 3,
    },

    // Summary box item style
    summaryBoxItem: {
        p: 1,
        borderRight: '1px solid #E0E0E0',
        bgcolor: '#F3F3F3',
        borderRadius: '6px',
        width: '100%',
    },

    // Summary item typography style
    summaryItemTypography: {
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '150%',
        letterSpacing: '0.15px',
        color: 'rgba(0, 0, 0, 0.87)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
    },

    // New style for borderBottom none
    noBorderBottom: {
        borderBottom: 'none',
    },
};

const BatchSelectionModal = ({ usedBatchQty, serviceListPayload, outerIndex, innerIndex, open, onClose, inventoryId, onSave, fetchAPi = () => {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    //hooks
    const [isUsedInvoiceQty, setIsUsedInvoiceQty] = useState(null);
    const [isUseLastCost, setIsUseLastCost] = useState(false);
    const [openTrackedIncreaseModal, setOpenTrackedIncreaseModal] = useState(false);
    const [addQty, setAddQty] = useState(false);
    const [disabledFields, setDisabledFields] = useState([]);
    const [partList, setPartList] = useState([]);
    const [rows, setRows] = useState([]);
    const [isLoader, setLoader] = useState(false);

    const [priceUpdateLoader, setPriceUpdateLoader] = useState(false);

    const [showLoader, setShowLoader] = useState(false);
    const [batchNumber, setBatchNumber] = useState(null);
    const [inventoryDetail, setInventoryDetail] = useState(null);
    const [openInventoryModel, setOpenInventoryModal] = useState(false);

    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [partInvoiceModal, setPartInvoiceModal] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const debouncePriceTimerRef = useRef(null);
    const previousPriceRef = useRef(null);
    const [openPriceChangeModal, setOpenPriceChangeModal] = useState({ open: false, outerIndex: 0, innerIndex: 0 });
    // Sort parameters for table columns
    const [sortParams, setSortParams] = useState([
        { key: 'supplierInvoice', orderBy: 'DESC' },
        { key: 'qty', orderBy: 'DESC' },
    ]);

    // Calculate totals
    const totals = useMemo(() => {
        return rows.reduce(
            (acc, row) => {
                if (row.isSelected && row.batchQty > 0) {
                    acc.quantity += Number(formatNumberAsWhole(row.batchQty));
                    acc.amount += Number(row.batchCost) * Number(formatNumberAsWhole(row.batchQty));
                }
                return acc;
            },
            { quantity: 0, amount: 0 }
        );
    }, [rows]);

    const fetchPartList = useCallback(async () => {
        try {
            setLoader(true);
            const resp = await dispatch(
                getInvcentoryPartDetailAPI({
                    inventory: inventoryId?._id,
                    groupByBatchNumber: true,
                })
            );

            if (resp?.length > 0) {
                setPartList(resp);
                const updatePartList = resp.map((item) => {
                    let selectedBatchArray = [];
                    let selectedItem = null;
                    let batchCost = null;

                    if (inventoryId?.batchArray?.length > 0) {
                        inventoryId?.batchArray.forEach((batch) => {
                            if (batch.batchNumber === item.batchNumber) {
                                const hasQty = batch.batchQty != null && Number(batch.batchQty) > 0;
                                if (batch.isSelected || hasQty) {
                                    selectedItem = batch;
                                    selectedBatchArray.push(batch.batchNumber);
                                }
                            }
                        });
                        // batchCost = inventoryId?.batchArray[0].batchCost;
                        // selectedItem = inventoryId?.batchArray.find((selectedItem) => item.batchNumber.includes(selectedItem.batchNumber));
                    }
                    let batchQtyUsed = {};
                    serviceListPayload.forEach((serviceItem, serviceIndex) => {
                        if (serviceItem.parts?.length > 0) {
                            serviceItem.parts.forEach((part, index) => {
                                if (part.batchArray?.length > 0) {
                                    let batchSelect = null;
                                    part.batchArray.forEach((batch, ind) => {
                                        batchSelect = serviceListPayload[serviceIndex]?.parts[index].batchArray.find((selectedItem) =>
                                            item.batchNumber.includes(selectedItem.batchNumber)
                                        );

                                        // If this batch number matches our current item

                                        if (batch.batchNumber === item.batchNumber && batch.batchNumber === batchSelect.batchNumber) {
                                            // Add the quantity used in this service to our total

                                            if (
                                                batchSelect.outerIndex === outerIndex &&
                                                batchSelect.innerIndex === innerIndex &&
                                                batchSelect.batchIndex === ind
                                            ) {
                                            } else {
                                                // batchQtyUsed[batchSelect.batchNumber] = (batchQtyUsed[batchSelect.batchNumber] || 0) + (batch?.batchQty || 0);
                                                // This seemms to be important opne this later if batch mismmatch qty issue comes
                                                // batchQtyUsed[batchSelect.batchNumber] = NumericEngine.add(
                                                //     Number(batchQtyUsed[batchSelect.batchNumber] || 0),
                                                //     Number(batch?.batchQty || 0)
                                                // );
                                            }
                                        }
                                    });
                                }
                            });
                        }
                    });
                    const mappedItem = {
                        ...item,
                        // inStock: item.inStock - (batchQtyUsed[item.batchNumber] || 0) + (usedBatchQty[item.batchNumber] || 0) || 0,
                        inStock:
                            NumericEngine.add(
                                NumericEngine.subtract(Number(item.inStock || 0), Number(batchQtyUsed[item.batchNumber] || 0)),
                                Number(usedBatchQty[item.batchNumber] || 0)
                            ) || 0,
                        isSelected: selectedItem?.batchNumber === item?.batchNumber,
                        batchQty: selectedItem?.batchQty || 0,
                        batchCost: batchCost || NumericEngine.format(item?.inventory?.lastRetailPrice || 0) || '0',
                    };

                    return mappedItem;
                });

                setRows(updatePartList);
            }
        } catch (error) {
        } finally {
            setLoader(false);
        }
    }, [dispatch, inventoryId]);

    useEffect(() => {
        if (open && inventoryId) {
            fetchPartList();
        }
    }, [fetchPartList, open, inventoryId]);

    /**
     * Handles sorting for table columns
     */
    const handleSortRequest = (key) => {
        setSortParams((prevSortParams) => {
            // Find the existing sort index
            const existingSortIndex = prevSortParams.findIndex((param) => param.key === key);

            const newSortParams = [...prevSortParams];

            if (existingSortIndex >= 0) {
                // Toggle the order if the key already exists
                const updatedSort = {
                    ...newSortParams[existingSortIndex],
                    orderBy: newSortParams[existingSortIndex].orderBy === 'ASC' ? 'DESC' : 'ASC',
                };

                // Remove from current position and add to the start
                newSortParams.splice(existingSortIndex, 1);
                newSortParams.unshift(updatedSort);
            } else {
                // Add new sort option to the top
                newSortParams.unshift({ key, orderBy: 'ASC' });
            }

            return newSortParams;
        });
    };

    /**
     * Checks if a column is actively sorted
     * @param {string} key - Column key to check
     * @returns {boolean}
     */
    const isActiveSortColumn = (key) => sortParams.some((param) => param.key === key);

    /**
     * Gets the sort direction for a column
     * @param {string} key - Column key to check
     * @returns {string} - 'asc' or 'desc'
     */
    const getSortDirection = (key) => {
        const param = sortParams.find((param) => param.key === key);
        return param?.orderBy === 'ASC' ? 'asc' : 'desc';
    };

    // Handle quantity input change for individual rows
    const handleInputChange = (batchNumber, field, value) => {
        setRows((prevRows) =>
            prevRows.map((row) => (row.batchNumber === batchNumber ? { ...row, [field]: value, isSelected: value === 0 ? false : true } : row))
        );
    };

    const handleClosePriceChangeModal = (outerIndex, innerIndex) => {
        const clone = JSON.parse(JSON.stringify(rows));
        clone[outerIndex].batchCost = clone[outerIndex].batchCost || 0;

        setRows(clone);
        setOpenPriceChangeModal({
            open: false,
            outerIndex: 0,
            innerIndex: 0,
        });
        previousPriceRef.current = null;
    };

    // Handle global price change - update all rows
    const handleGlobalPriceChange = (value) => {
        setRows((prevRows) => prevRows.map((row) => (row.isSelected ? { ...row, batchCost: value } : { ...row, batchCost: value })));
    };

    /**
     * Renders part summary information
     */
    const renderPartSummary = () => (
        <Box sx={styles.summaryBoxContainer}>
            <Grid container spacing={1.5}>
                {/* Part Number */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Part #</span>{' '}
                            {rows?.[0]?.inventory?.partNumber || rows?.[0]?.inventory?.tyreModel?.tyreDimension || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Part Name */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Part Name</span> {rows?.[0]?.inventory?.part?.partName || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Category / Model */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={{ ...styles.summaryBoxItem, borderRight: 'none' }}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>{rows?.[0]?.inventory?.partsType === PART_TYPES.PARTS ? 'Category' : 'Model'}</span>{' '}
                            {rows?.[0]?.inventory?.partsCategory?.name || rows?.[0]?.inventory?.tyreModel?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Supplier Name */}
                <Grid item xs={12} sm={8} md={8}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Supplier Name</span> {rows?.[0]?.supplierInvoice?.supplier?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Brand */}
                <Grid item xs={12} sm={4} md={4}>
                    <Box sx={styles.summaryBoxItem}>
                        <Typography sx={styles.summaryItemTypography}>
                            <span style={{ fontWeight: 400 }}>Brand</span> {rows?.[0]?.inventory?.partsBrand?.name || '-'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );

    const renderrCommonTableHead = () => (
        <TableHead style={{ display: 'hidden' }}>
            <TableRow sx={{ height: '56px', background: '#F8F8F8!important' }}>
                {renderTableHeaderCell('', 30)}
                {rows?.length > 0 && rows[0]?.isTracked && renderTableHeaderCell('Invoice #', 83)}

                {renderTableHeaderCell('In Stock', 80)}
                {renderTableHeaderCell('Quantity', 100, null, false, 'left', false, 120)}
                {renderTableHeaderCell('Cost', 80)}
                {renderTableHeaderCell('Fee', 80)}
                {renderTableHeaderCell('Price', 111, null, false, '', false)}
                {renderTableHeaderCell('Amount', 110, null, false, 'right', false)}

                <TableCell align="right" sx={{ ...styles.tableHeaderCell }} />
            </TableRow>
        </TableHead>
    );

    /**
     * Renders table header cells with sort functionality
     * @param {string} label - Column label
     * @param {string} sortKey - Key for sorting
     * @param {boolean} sortable - Whether column is sortable
     * @returns {React.Component}
     */
    const renderTableHeaderCell = (label, width = '', sortKey = null, sortable = false, align = 'left', hideBorderBottom = false, maxWidth = '') => (
        <TableCell
            sx={{
                ...styles.tableHeaderCell,
                ...(hideBorderBottom ? styles.noBorderBottom : {}),
            }}
            style={{ minWidth: width, width: maxWidth }}
            align={align}
        >
            {sortable ? (
                <TableSortLabel active={isActiveSortColumn(sortKey)} direction={getSortDirection(sortKey)} onClick={() => handleSortRequest(sortKey)}>
                    {label}
                </TableSortLabel>
            ) : (
                label
            )}
        </TableCell>
    );

    /**
     * Renders a standard table body cell with typography
     * @param {string|number} content - Cell content
     * @returns {React.Component}
     */
    const renderTableBodyCell = (content, font, isLastRow = false) => (
        <TableCell
            sx={{
                ...styles.tableBodyCell,
                ...(isLastRow ? styles.noBorderBottom : {}),
            }}
        >
            <Typography
                sx={{
                    ...styles.tableCellTypography,
                    fontFamily: font ? '16px' : '',
                    fontWeight: font ? 600 : '',
                }}
            >
                {content}
            </Typography>
        </TableCell>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth className="commonDialog__900">
                {/* Dialog Header */}
                <DialogTitle sx={styles.dialogHeader}>
                    <Typography variant="h6">Select which part you want to add</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon style={{ fill: 'black', fontSize: 32 }} />
                    </IconButton>
                </DialogTitle>

                {/* Dialog Content */}
                <DialogContent sx={styles.dialogContent}>
                    {/* Part Summary Information */}
                    {renderPartSummary()}

                    {/* Part Inventory Table */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            mt: 2,
                            height: '100%',
                            maxHeight: '40.5vh',
                            overflow: 'auto',
                            boxShadow: 'none',
                        }}
                    >
                        <Table size="small" stickyHeader>
                            {/* Header */}
                            {renderrCommonTableHead()}

                            {/* Body */}
                            <TableBody>
                                {isLoader ? (
                                    <TableRowsLoader height={'76px'} rowsNum={3} cellsNum={7} />
                                ) : rows && rows.length > 0 ? (
                                    rows.map((row, index) => (
                                        <TableRow
                                            key={row._id}
                                            sx={{ height: '76px', cursor: 'pointer' }}
                                            onClick={() => {
                                                // setBatchNumber(row?.batchNumber);
                                                // setOpenInventoryModal(true);
                                            }}
                                        >
                                            <TableCell style={{ padding: '0' }}>
                                                <Checkbox
                                                    disabled={row.inStock < 0.1}
                                                    checked={row.isSelected}
                                                    onChange={() => {
                                                        let clone = JSON.parse(JSON.stringify(rows));
                                                        if (row.inStock > 0 && row.batchQty === 0) {
                                                            if (row.inStock >= 1) {
                                                                clone[index].batchQty = 1;
                                                            } else if (row.inStock >= 0.1) {
                                                                clone[index].batchQty = 0.1;
                                                            }
                                                        }
                                                        clone[index].isSelected = !clone[index].isSelected;
                                                        setRows(clone);
                                                    }}
                                                />
                                            </TableCell>
                                            {/* Supplier Invoice Number */}
                                            {rows?.length > 0 &&
                                                rows[0]?.isTracked &&
                                                renderTableBodyCell(row.supplierInvoice?.invoiceNumber || '-', true, index === rows.length - 1)}

                                            {/* Quantity */}
                                            {renderTableBodyCell(row.inStock || 0, false, index === rows.length - 1)}

                                            {/* Used Quantity */}
                                            <TableCell
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                }}
                                            >
                                                <InputWithIcon
                                                    value={row.batchQty}
                                                    disabled={row.inStock < 0.1}
                                                    fontSize="14px"
                                                    borderRadius={'4px'}
                                                    skipOnChange={true}
                                                    onChange={(numericValue) => {
                                                        if (numericValue === '' || /^\d*(\.\d{0,1})?$/.test(numericValue)) {
                                                            if (row.inStock < Number(numericValue)) {
                                                                if (!row.isTracked) {
                                                                    toast.error(
                                                                        'You do not have sufficient quantity in your inventory. Please add additional quantity to continue',
                                                                        {
                                                                            icon: <InfoOutlined sx={{ fontSize: '22px' }} />,
                                                                        }
                                                                    );
                                                                    setBatchNumber(row?.batchNumber);
                                                                    setAddQty(true);
                                                                    setIsUsedInvoiceQty(row?.batchQty || 0);
                                                                    setOpenInventoryModal(true);
                                                                    return;
                                                                } else {
                                                                    setBatchNumber(row?.batchNumber);
                                                                    setOpenTrackedIncreaseModal(true);
                                                                    return;
                                                                }
                                                            }
                                                            handleInputChange(row.batchNumber, 'batchQty', numericValue);
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Cost */}
                                            <TableCell
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                }}
                                            >
                                                {row.cost ? NumericEngine.format(row?.cost || 0, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                                            </TableCell>

                                            {/* Fee */}
                                            <TableCell
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                }}
                                            >
                                                {row?.inventory?.feeAmount
                                                    ? NumericEngine.format(row?.inventory?.feeAmount || 0, { formatCurrency: true, trailingZeros: true })
                                                    : '$0.00'}
                                            </TableCell>

                                            {/* retail price */}
                                            <TableCell
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                }}
                                            >
                                                {row.batchCost
                                                    ? NumericEngine.format(row?.batchCost || 0, { formatCurrency: true, trailingZeros: true })
                                                    : '$0.00'}
                                            </TableCell>

                                            {/* Cost */}
                                            <TableCell
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                }}
                                                align="right"
                                            >
                                                {NumericEngine.format(NumericEngine.multiply(Number(row?.batchCost || 0), Number(row?.batchQty || 0)), {
                                                    formatCurrency: true,
                                                    trailingZeros: true,
                                                })}
                                            </TableCell>

                                            {/* Action buttons */}
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    ...styles.tableBodyCell,
                                                    ...(index === rows.length - 1 ? styles.noBorderBottom : {}),
                                                    paddingLeft: '0',
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setDisabledFields(['partName', 'partNumber', 'supplierId', 'supplierInvoiceNumber']);
                                                        setBatchNumber(row?.batchNumber);
                                                        setIsUsedInvoiceQty(row?.batchQty || 0);
                                                        setOpenInventoryModal(true);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" style={{ color: '#646466' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography sx={styles.tableCellTypography}>No Data</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                            {/* Footer */}
                            <TableFooter style={{ position: 'sticky', bottom: '0', background: '#fff' }}>
                                {/* Total Header Row */}
                                <TableRow sx={{ height: '32px', borderBottom: 'none' }}>
                                    {renderTableHeaderCell('', 30, null, false, '', true)}
                                    {rows?.length > 0 && rows[0]?.isTracked && renderTableHeaderCell('', 83, null, false, '', true)}
                                    {renderTableHeaderCell('', 80, null, false, '', true)}

                                    <TableCell
                                        colSpan={1}
                                        sx={{
                                            ...styles.tableHeaderTotalCell,
                                            padding: '0 12px',
                                            ...styles.noBorderBottom,
                                            minWidth: '100px',
                                        }}
                                    >
                                        <Typography fontWeight={500} fontSize={14} lineHeight={'24px'} style={{ color: '#000' }}>
                                            Quantity
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell
                                        colSpan={1}
                                        sx={{
                                            ...styles.tableHeaderTotalCell,
                                            padding: '0 12px',
                                            ...styles.noBorderBottom,
                                            minWidth: '100px',
                                        }}
                                    >
                                        <Typography fontWeight={500} fontSize={14} lineHeight={'24px'} style={{ color: '#000' }}>
                                            Fee
                                        </Typography>
                                    </TableCell> */}
                                    {renderTableHeaderCell('', 80, null, false, '', true)}
                                    {renderTableHeaderCell('Fee', 80, null, false, '', true)}

                                    <TableCell
                                        colSpan={1}
                                        sx={{
                                            ...styles.tableHeaderTotalCell,
                                            ...styles.noBorderBottom,
                                            minWidth: '111px',
                                        }}
                                        width={'111px'}
                                    >
                                        <Typography fontWeight={500} fontSize={14} lineHeight={'24px'} style={{ color: '#000' }}>
                                            Price
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        colSpan={1}
                                        sx={{
                                            ...styles.tableHeaderTotalCell,
                                            ...styles.noBorderBottom,
                                            minWidth: '110px',
                                        }}
                                        width={'110px'}
                                        align="right"
                                    >
                                        <Typography fontWeight={500} fontSize={14} lineHeight={'24px'} style={{ color: '#000' }}>
                                            Total Amount
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right" width={20} sx={{ ...styles.tableHeaderCell, ...styles.noBorderBottom }} />
                                </TableRow>

                                {/* Total Values Row */}
                                <TableRow sx={{ height: '60px', borderBottom: 'none' }}>
                                    <TableCell colSpan={3} sx={{ ...styles.noBorderBottom }}></TableCell>

                                    <TableCell colSpan={1} sx={{ ...styles.noBorderBottom, color: '#000', fontSize: '14px' }}>
                                        {NumericEngine.format(totals.quantity, {
                                            formatCurrency: false,
                                            trailingZeros: false,
                                        })}
                                    </TableCell>

                                    <TableCell colSpan={1} sx={{ ...styles.noBorderBottom }}></TableCell>
                                    <TableCell colSpan={1} sx={{ ...styles.noBorderBottom, color: '#000', fontSize: '14px' }}>
                                        {NumericEngine.format(NumericEngine.multiply(Number(totals.quantity), Number(rows[0]?.inventory?.feeAmount || 0)), {
                                            formatCurrency: true,
                                            trailingZeros: true,
                                        })}
                                    </TableCell>
                                    <TableCell
                                        colSpan={1}
                                        sx={{
                                            ...styles.noBorderBottom,
                                            paddingRight: '0',
                                        }}
                                    >
                                        <InputWithIcon
                                            fontSize="14px"
                                            value={rows[0]?.batchCost}
                                            borderRadius={'4px'}
                                            skipOnChange
                                            onChange={(numericValue) => {
                                                if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                                                    handleGlobalPriceChange(numericValue);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell
                                        colSpan={1}
                                        align="right"
                                        sx={{
                                            ...styles.noBorderBottom,
                                            color: '#000',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {NumericEngine.format(totals?.amount || 0, { formatCurrency: true, trailingZeros: true })}
                                    </TableCell>
                                    <TableCell sx={{ ...styles.noBorderBottom }}></TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions style={{ padding: '0 24px 24px 24px' }}>
                    <Stack justifyContent={'space-between'} direction={'row'} gap={1.5} width={'100%'}>
                        <Button
                            variant="contained"
                            color="greyColor"
                            startIcon={<Add />}
                            onClick={() => {
                                if (inventoryId?.batchNumber?.length > 0) {
                                    setBatchNumber(inventoryId.batchNumber[0]);
                                }
                                setIsUseLastCost(true);
                                setDisabledFields(['partName', 'partNumber', 'supplierId']);
                                setIsUsedInvoiceQty(0);
                                setOpenInventoryModal(true);
                            }}
                            style={{
                                fontWeight: 500,
                                lineHeight: '24px',
                                letterSpacing: '0.04px',
                                fontSize: '14px',
                                height: '42px',
                                color: '#000000',
                                boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                            }}
                        >
                            Add More Stock
                        </Button>
                        <Stack justifyContent={'flex-end'} direction={'row'} gap={1.5}>
                            <Button color="primary" variant="outlined" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => {
                                    onSave(rows);
                                }}
                            >
                                ADD TO INVOICE
                            </Button>
                        </Stack>
                    </Stack>
                </DialogActions>
            </Dialog>

            <CommonDialog
                dialogClaassName="commonDialog commonDialog__560"
                open={openTrackedIncreaseModal}
                onClose={() => {
                    //cancl button fuyncituon
                    setDisabledFields([]);
                    setBatchNumber(null);
                    setOpenTrackedIncreaseModal(false);
                }}
                onSave={() => {
                    //update button funtion
                    // setBatchNumber(null);

                    // setSelectedInventoryId(currentPart?._id);
                    // setSaveIndexes({
                    //     outerIndex,
                    //     innerIndex: innerIndex,
                    // });edit
                    setDisabledFields(['partName', 'partNumber', 'supplierId', 'supplierInvoiceNumber']);
                    setAddQty(true);
                    setIsUsedInvoiceQty(0);
                    setOpenInventoryModal(true);

                    // setOpenTrackedIncreaseModal(false);
                }}
                onConfirm={() => {
                    //crete new button fucntion
                    // setBatchNumber(null);
                    setIsUseLastCost(true);
                    setDisabledFields(['partName', 'partNumber', 'supplierId']);

                    // setSaveIndexes({
                    //     outerIndex,
                    //     innerIndex: innerIndex + 1,
                    // });
                    setIsUsedInvoiceQty(0);
                    setOpenInventoryModal(true);
                    // setOpenTrackedIncreaseModal(false);
                }}
                dialogTitle={'Not Enough Inventory – Update or Add New?'}
                dialogSubtitle={'You’re trying to add additional quantity to this invoice, but there’s not enough inventory available.'}
                dialogSubtitle2={
                    'Would you like to add more quantity to the existing inventory at the same cost, or create a new entry with a different cost from a new supplier invoice?'
                }
                saveButtonText="UPDATE Existing"
                saveButtonWidth="176px"
                saveButtonType="primary"
                confirmButtonText="Create New"
                confirmButtonWidth="140px"
            />
            {/* price update modal  */}
            <CommonDialog
                dialogClaassName="commonDialog commonDialog__600"
                open={openPriceChangeModal.open}
                iconClose={true}
                onIconClose={() => {
                    const clone = JSON.parse(JSON.stringify(rows));
                    clone[openPriceChangeModal.outerIndex].batchCost = previousPriceRef.current;

                    setRows(clone);
                    setOpenPriceChangeModal({
                        open: false,
                        outerIndex: 0,
                        innerIndex: 0,
                    });
                    previousPriceRef.current = null;
                }}
                onClose={() => {
                    handleClosePriceChangeModal(openPriceChangeModal.outerIndex, openPriceChangeModal.innerIndex);
                }}
                onSave={async () => {
                    try {
                        setPriceUpdateLoader(true);
                        const clone = JSON.parse(JSON.stringify(rows));

                        let batchNumber = clone[openPriceChangeModal.outerIndex].batchNumber;

                        const values = await dispatch(getInventoryBatchDetailApi(batchNumber));

                        const inventoryId = values?.inventory?._id || values?.inventoryId || values?._id;

                        const batchN = batchNumber || undefined;
                        const partCost = clone[openPriceChangeModal.outerIndex].batchCost || 0;
                        const pricingMethod = values?.pricingMethod || values?.inventory?.lastPricingMethod;
                        const cost = Number(values.cost);
                        let markupOrMarginPercentage = 0;
                        if (pricingMethod === 'markup') {
                            markupOrMarginPercentage = cost > 0 ? ((partCost - cost) / cost) * 100 : 0;
                        } else if (pricingMethod === 'margin') {
                            markupOrMarginPercentage = partCost > 0 ? ((partCost - cost) / partCost) * 100 : 0;
                        }
                        let inventoryPayload = {
                            partsType: values?.inventory?.partsType,
                            partName: values?.inventory?.part?.partName || values?.part?.partName || values?.part?.name || undefined,
                            partsBrandName: values?.inventory?.partsBrand?.name || undefined,
                            partsCategoryName: values?.inventory?.partsCategory?.name || undefined,
                            partsProvider: values?.inventory?.lastPartsProvider || undefined,
                            supplierId: values?.supplier || undefined,
                            supplierInvoiceNumber: values?.supplier ? values?.supplierInvoice?.invoiceNumber || undefined : undefined,
                            partNumber: values.partNumber || undefined,
                            description: values?.inventory?.lastDescription || undefined,
                            qty: Number(values.inStock) || 0,
                            cost: cost,
                            pricingMethod: pricingMethod,
                            markupOrMarginPercentage: (markupOrMarginPercentage = Number(markupOrMarginPercentage)),
                        };
                        if (values?.inventory?.feeAmount > 0) {
                            inventoryPayload.feeName = values?.inventory?.fee?.name || undefined;
                            inventoryPayload.feeAmount = Number(values?.inventory?.feeAmount) || 0;
                        }
                        if (values?.partsType === PART_TYPES.TYRE) {
                            inventoryPayload.partNumber = undefined;
                            inventoryPayload.partsCategoryName = undefined;
                            inventoryPayload.tyreModelName = values?.inventory?.tyreModel?.name || undefined;
                            inventoryPayload.tyreDimension = values.tyreDimension || undefined;
                        }
                        const resp = await updateInventoryApi(inventoryId, batchN, filterUndefined(inventoryPayload));

                        if (resp?.data?._id) {
                            clone[openPriceChangeModal.outerIndex].batchCost = partCost;

                            setRows(clone);
                        }
                        setPriceUpdateLoader(false);
                        setOpenPriceChangeModal({
                            open: false,
                            outerIndex: 0,
                            innerIndex: 0,
                        });
                        previousPriceRef.current = null;
                    } catch (error) {
                        console.log('🚀 ~ onSave={ ~ error:', error);
                    } finally {
                        setPriceUpdateLoader(false);
                        setOpenPriceChangeModal({
                            open: false,
                            outerIndex: 0,
                            innerIndex: 0,
                        });
                        handleClosePriceChangeModal(openPriceChangeModal.outerIndex, openPriceChangeModal.innerIndex);
                    }
                }}
                loader={priceUpdateLoader}
                dialogTitle="Price Change Detected"
                dialogSubtitle="The price you entered doesn’t match the current inventory price."
                dialogSubtitle2={' Would you like to apply this change only for this invoice, or update the inventory price for this part going forward?'}
                cancelButtonText="ONLY FOR THIS INVOICE"
                cancelButtonWidth="223px"
                saveButtonText="UPDATE ALL INVENTORY"
                saveButtonType="primary"
                saveButtonWidth="220px"
            />
            <AddInventoryModal
                addQty={addQty}
                showUsedInInvoiceFeild={true}
                FeildLabelName={'Added to Invoice'}
                isUsedInvoiceQty={isUsedInvoiceQty}
                setIsUsedInvoiceQty={setIsUsedInvoiceQty}
                isUseLastCost={isUseLastCost}
                conflictResolutionProp={disabledFields.length === 3 ? 'create_new_part' : disabledFields.length === 4 ? 'update_existing_part' : null}
                disabledFields={disabledFields}
                type={INVENTORY_TYPES.INVENTORY}
                inventoryId={inventoryId?._id}
                batchNumber={batchNumber}
                openModal={openInventoryModel}
                closeModal={() => {
                    setDisabledFields([]);
                    setAddQty(false);
                    setIsUseLastCost(false);
                    setIsUsedInvoiceQty(null);
                    setOpenInventoryModal(false);
                    // setBatchNumber(null);
                }}
                onSubmitModal={() => {
                    fetchPartList();
                    setDisabledFields([]);
                    setIsUseLastCost(false);
                    setIsUsedInvoiceQty(null);
                    setOpenInventoryModal(false);
                    setBatchNumber(null);
                }}
                hideTabs
                setShowLoader={setShowLoader}
                showLoader={showLoader}
                fetchApi={async (data) => {
                    setOpenTrackedIncreaseModal(false);
                    setIsUseLastCost(false);

                    if (data) {
                        const resp = await dispatch(
                            getInvcentoryPartDetailAPI({
                                inventory: inventoryId?._id,
                                groupByBatchNumber: true,
                            })
                        );

                        if (resp?.length > 0) {
                            setPartList(resp);

                            // Which batches already exist in current rows (before this fetch)
                            const existingBatchNumbers = new Set(rows.map((row) => row.batchNumber));
                            // Batches in resp that are NEW (not in current rows) = the batch we just added via "Add More Stock"
                            const newBatchesInResp = resp.filter((part) => !existingBatchNumbers.has(part.batchNumber));

                            // Determine which batch was actually added/updated so we assign addedQty to the correct one.
                            // Bug: "Add More Stock" sets batchNumber = inventoryId.batchNumber[0] (often the first/0-stock batch).
                            // If API returns data.batchNumber as an array of all batches, we must NOT use state batchNumber,
                            // or the 0-stock batch gets marked "updated" and receives the quantity instead of the new batch.
                            let addedBatchNumber = batchNumber;
                            if (data?.batchNumber) {
                                if (Array.isArray(data.batchNumber)) {
                                    if (data.batchNumber.length === 1) {
                                        addedBatchNumber = data.batchNumber[0];
                                    } else if (data.batchNumber.length > 1) {
                                        // Multiple batches in response: prefer the batch that is NEW in resp (the one we added).
                                        if (newBatchesInResp.length === 1) {
                                            addedBatchNumber = newBatchesInResp[0].batchNumber;
                                        } else if (newBatchesInResp.length > 1) {
                                            const newBatchNumbers = new Set(newBatchesInResp.map((p) => p.batchNumber));
                                            const fromData = data.batchNumber.find((bn) => newBatchNumbers.has(bn));
                                            addedBatchNumber = fromData ?? newBatchesInResp[0].batchNumber;
                                        } else {
                                            // No new batches: we updated an existing batch (e.g. added qty to same batch).
                                            if (batchNumber && data.batchNumber.includes(batchNumber)) {
                                                addedBatchNumber = batchNumber;
                                            } else {
                                                addedBatchNumber = data.batchNumber[0];
                                            }
                                        }
                                    }
                                } else {
                                    addedBatchNumber = data.batchNumber;
                                }
                            }
                            // Get the quantity - prefer isUsedInvoiceQty if set (this is the quantity entered in the modal when editing)
                            // otherwise use data.qty, otherwise 0
                            const addedQty =
                                isUsedInvoiceQty !== null && isUsedInvoiceQty !== undefined && isUsedInvoiceQty !== ''
                                    ? Number(isUsedInvoiceQty || 0)
                                    : data?.qty
                                      ? Number(data.qty)
                                      : 0;

                            const updatedRows = rows.map((row) => {
                                const matchedPart = resp.find((part) => part.batchNumber === row.batchNumber);

                                if (matchedPart) {
                                    // Check if this is the batchNumber that was just updated/added
                                    // Compare with the specific batchNumber (not array)
                                    const isUpdatedBatch = addedBatchNumber && matchedPart.batchNumber === addedBatchNumber;

                                    // Get the quantity to use - if this is the updated batch, use addedQty (from isUsedInvoiceQty or data.qty)
                                    // IMPORTANT: When editing, we always use the isUsedInvoiceQty value (even if 0) to respect user input
                                    let newBatchQty;
                                    if (isUpdatedBatch) {
                                        // This is the batch that was just edited/updated
                                        // Use the quantity from isUsedInvoiceQty (the value entered in the modal)
                                        // This handles both editing existing batches and adding new stock
                                        newBatchQty = addedQty;
                                    } else {
                                        // Preserve existing batchQty, but ensure it doesn't exceed inStock
                                        // Also, if inStock is 0, batchQty must be 0
                                        if (matchedPart.inStock === 0 || matchedPart.inStock < 0.1) {
                                            newBatchQty = 0;
                                        } else {
                                            newBatchQty = row.batchQty;
                                            if (row.batchQty > matchedPart.inStock) {
                                                newBatchQty = matchedPart.inStock;
                                            }
                                        }
                                    }

                                    // Determine isSelected:
                                    // - If this is the updated batch: automatically select if quantity > 0 (user edited it, so they want it selected)
                                    // - If this is NOT the updated batch: preserve existing selection state BUT ensure it's still valid
                                    //   (batchQty > 0 and sufficient inStock, and row was previously selected)
                                    let shouldBeSelected;
                                    if (isUpdatedBatch) {
                                        // For the updated/edited batch, automatically select if quantity > 0
                                        // This ensures that when user edits and sets quantity to 5, it gets selected
                                        shouldBeSelected = newBatchQty > 0;
                                    } else {
                                        // For other batches, preserve selection state BUT ensure it's still valid
                                        // A row should only be selected if:
                                        // 1. It was previously selected
                                        // 2. It has batchQty > 0
                                        // 3. There's sufficient inStock (at least as much as batchQty)
                                        // 4. inStock is > 0 (can't select rows with 0 stock)
                                        shouldBeSelected = row.isSelected && newBatchQty > 0 && matchedPart.inStock > 0 && matchedPart.inStock >= newBatchQty;
                                    }
                                    return {
                                        ...row,
                                        cost: matchedPart.cost,
                                        batchCost: matchedPart?.inventory?.lastRetailPrice,
                                        batchQty: newBatchQty,
                                        inStock: matchedPart.inStock,
                                        isSelected: shouldBeSelected,
                                        inventory: {
                                            ...matchedPart.inventory,
                                            feeAmount: matchedPart.inventory.feeAmount,
                                        },
                                    };
                                }
                                // If no match found, keep the row as is (preserve selection state and batchQty)
                                return row;
                            });

                            // Identify new batchNumbers in resp not present in existing rows (existingBatchNumbers already computed above)
                            const newRows = resp
                                .filter((part) => !existingBatchNumbers.has(part.batchNumber))
                                .map((part) => {
                                    // For new rows, if this is the batchNumber that was just added, use addedQty
                                    const isNewBatch = addedBatchNumber && part.batchNumber === addedBatchNumber;
                                    let newBatchQty = 0;

                                    if (isNewBatch && addedQty > 0) {
                                        // This is the batch that was just added - use addedQty
                                        newBatchQty = addedQty;
                                    } else {
                                        // This is a different new batch (shouldn't happen when adding stock, but handle it)
                                        // Set default quantity based on stock availability
                                        newBatchQty = part.inStock >= 1 ? 1 : part.inStock >= 0.1 ? 0.1 : 0;
                                    }

                                    return {
                                        ...part,
                                        isSelected: newBatchQty > 0,
                                        batchQty: newBatchQty,
                                        batchCost: rows[0]?.batchCost || part?.inventory?.lastRetailPrice || 0,
                                    };
                                });

                            setRows([...updatedRows, ...newRows]);
                        }
                    }
                    setIsUsedInvoiceQty(null);
                    setBatchNumber(null);
                }}
                ShowAllTabs={false}
                ShowTabIndex={0}
                modalType="add"
            />

            <PartUsedInvoiceModal
                open={partInvoiceModal}
                onClose={() => {
                    setPartInvoiceModal(false);
                }}
                invoiceData={customerInvoices}
            />
            <PartUsedInvoiceModal
                open={partInvoiceModal}
                onClose={() => {
                    setPartInvoiceModal(false);
                }}
                invoiceData={customerInvoices}
            />

            <CommonDialog
                loader={isDeleting}
                open={showDeleteModal}
                onClose={() => {
                    setInventoryDetail(null);
                    setBatchNumber(null);
                    setShowDeleteModal(false);
                }}
                onSave={async () => {
                    let resp;

                    if (!inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice) {
                        setPartInvoiceModal(true);
                        return;
                    }
                    setIsDeleting(true);
                    if (!inventoryDetail?.isTracked) {
                        resp = await deleteInventoryApi(inventoryDetail?.inventory?._id, inventoryDetail?.batchNumber);
                    } else if (inventoryDetail?.supplierInvoice?._id) {
                        navigate(
                            `/suppliers-invoice/${
                                inventoryDetail?.supplierInvoice?.paymentStatus === 'open'
                                    ? 'open'
                                    : inventoryDetail?.supplierInvoice?.paymentStatus === 'partial_payment'
                                      ? 'partial'
                                      : 'paid'
                            }/` + inventoryDetail.supplierInvoice._id
                        );
                    }
                    if (resp) {
                        setInventoryDetail(null);
                        setBatchNumber(null);
                        setShowDeleteModal(false);
                        fetchPartList();
                    }
                    setIsDeleting(false);
                }}
                dialogClaassName={inventoryDetail?.isUsedInCustomerInvoice || !inventoryDetail?.isTracked ? 'commonDialog' : 'commonDialog commonDialog__560'}
                dialogTitle={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice
                        ? '🚫 Cannot Delete Inventory'
                        : !inventoryDetail?.isTracked
                          ? 'Delete Part?'
                          : '⚠️ Part added from Supplier Invoice'
                }
                dialogSubtitle={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice
                        ? 'This part has been used in at least one customer invoice.'
                        : !inventoryDetail?.isTracked
                          ? 'Are you sure you want to delete this part from your inventory?'
                          : 'This part was originally added through a Supplier Invoice.'
                }
                dialogSubtitle2={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice
                        ? 'To preserve invoice history, deletion is not allowed. To hide this part, click View In-Stock Parts Only.'
                        : !inventoryDetail?.isTracked
                          ? ''
                          : 'To maintain accurate inventory and costing, please delete it directly from the Supplier Invoice.'
                }
                saveButtonText={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice
                        ? `view Invoice${customerInvoices.length > 1 ? 's' : ''}`
                        : !inventoryDetail?.isTracked
                          ? 'Delete'
                          : 'view supplier Invoice'
                }
                saveButtonType={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice ? 'primary' : !inventoryDetail?.isTracked ? 'error' : 'primary'
                }
                saveButtonWidth={
                    !inventoryDetail?.isTracked && inventoryDetail?.isUsedInCustomerInvoice ? '148px' : !inventoryDetail?.isTracked ? '103px' : '210px'
                }
            />
        </>
    );
};

export default BatchSelectionModal;
